import { CommandButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { type ShockProfile, type Vehicle, type VehicleDevice } from "../api";
import { Attribute } from "../ui/atoms/attribute";

import { FormUpdateVehicle } from "../ui/molecules/form-update-vehicle";
import { ListVehicleGroupAssignments } from "../ui/molecules/list-vehicle-group-assignments";
import { ListCommandButtonsVehicleGroupAssignments } from "../ui/organisms/list-command-buttons-vehicle-group-assignments";
import { createUuid, type UUID } from "../utils/uuid";

export type PageVehicleProps = { vehicleId: UUID };
import { Section } from "../ui/atoms/section";
import { ElofleetDialog } from "../ui/atoms/elofleet-dialog";
import { store } from "../store";
import { FusionLayout } from "@/parts/fusion-layout";

@observer
export class PageVehicle extends React.Component<PageVehicleProps> {
    @observable private accessor updateDialogVisible = false;

    // Create the UUID on this level, so the button bar is able to access the same state as the list.
    private vehicleGroupAssignmentListStateId = createUuid();

    constructor(props: PageVehicleProps) {
        super(props);
        makeObservable(this);
    }

    @computed private get vehicleId(): UUID {
        // NOTE: when rewriting as a functional component use useLocation hook instead
        return store.historyProvider.history.location.pathname.split("/").slice(-1)[0];
    }

    @computed private get vehicle(): Vehicle | undefined {
        return store.repositoryVehicles.byId(this.vehicleId);
    }

    @computed private get shockProfile(): ShockProfile | undefined {
        if (!this.vehicle || !this.vehicle.shockProfileId) {
            return;
        }
        return store.repositoryShockProfiles.byId(this.vehicle.shockProfileId);
    }

    @computed private get vehicleDevice(): VehicleDevice | undefined {
        if (!this.vehicle) {
            return;
        }
        return store.repositoryVehicleDevices.byVehicleId(this.vehicle.id);
    }

    @action.bound private showUpdateDialog(): void {
        this.updateDialogVisible = true;
    }

    @action.bound private hideUpdateDialog(): void {
        this.updateDialogVisible = false;
    }

    public render(): JSX.Element {
        if (!this.vehicle) {
            return <></>;
        }
        return (
            <FusionLayout breadcrumb={store.i18n.formatVehicle(this.vehicle)} classic>
                <Section withPadding>
                    <Attribute
                        title={store.i18n.t("page.vehicle.serialNumber")}
                        value={this.vehicle.serialNumber}
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.manufactureYear")}
                        value={this.vehicle.manufactureYear}
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.createdAt")}
                        value={store.i18n.formatDateTime(this.vehicle.createdAt)}
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.updatedAt")}
                        value={store.i18n.formatDateTime(this.vehicle.updatedAt)}
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.model")}
                        value={this.vehicle.model}
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.manufacturer")}
                        value={this.vehicle.manufacturer}
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.shockProfile")}
                        value={
                            this.shockProfile && store.i18n.formatShockProfile(this.shockProfile)
                        }
                    />
                    <Attribute
                        title={store.i18n.t("page.vehicle.vehicleDeviceLabel")}
                        value={this.vehicleDevice?.serialNumber}
                        fallback={store.i18n.t("page.vehicle.noVehicleDevice")}
                    />
                    <CommandButton
                        onClick={this.showUpdateDialog}
                        text={store.i18n.t("page.vehicle.edit")}
                    />
                </Section>

                <Section
                    title={store.i18n.t("page.vehicle.groups")}
                    actionButtons={
                        <ListCommandButtonsVehicleGroupAssignments
                            canDelete
                            vehicleId={this.vehicleId}
                            listStateId={this.vehicleGroupAssignmentListStateId}
                        />
                    }
                >
                    <ListVehicleGroupAssignments
                        vehicleId={this.vehicleId}
                        showVehicle={false}
                        listStateId={this.vehicleGroupAssignmentListStateId}
                    />
                </Section>

                {this.updateDialogVisible && (
                    <ElofleetDialog
                        isOpen={true}
                        title={store.i18n.t("page.vehicle.editDialog.title")}
                    >
                        {
                            <FormUpdateVehicle
                                asDialogContent
                                id={this.vehicle.id}
                                onUpdate={this.hideUpdateDialog}
                                onDialogCancel={this.hideUpdateDialog}
                            />
                        }
                    </ElofleetDialog>
                )}
            </FusionLayout>
        );
    }
}
